// Колоните на „Променени цени" — КЛИЕНТСКИ режим:
// `GET /reports/analytics/price-changes` връща целия период наведнъж, затова
// сортирането, филтрите в колоните и редът „Общо" се смятат в браузъра.
//
// Трите селекта над таблицата (потребител, артикул, посока) вече са филтри в
// САМИТЕ колони. Опциите им се подават ИЗРИЧНО, а не се вадят от фасетите:
// клетките „Продукт" и „Маса" носят обект с два реда и фасетният списък би
// ключовал по обект вместо по показания текст.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type {
  ChecklistOption,
  TextCellValue,
} from "../../../../components/dataTable";
import { PriceChangeAmount } from "./PriceChangeAmount";
import type { PriceChangeRow } from "./priceChangesMath";

const helper = createColumnHelper<PriceChangeRow>();

type Options = {
  /** Периодът е повече от един ден — датата излиза под часа. */
  isMultiDay: boolean;
  /** Всички потребители в отговора, не само в оцелелите след филтър редове. */
  userOptions: ChecklistOption[];
  itemOptions: ChecklistOption[];
};

export const usePriceChangesColumns = ({
  isMultiDay,
  userOptions,
  itemOptions,
}: Options) => {
  const { t } = useTranslation();

  const directionOptions = useMemo<ChecklistOption[]>(
    () => [
      {
        value: t("reportsPage.priceChangesDown"),
        label: t("reportsPage.priceChangesDown"),
      },
      {
        value: t("reportsPage.priceChangesUp"),
        label: t("reportsPage.priceChangesUp"),
      },
    ],
    [t],
  );

  const kindOptions = useMemo<ChecklistOption[]>(
    () => [
      {
        value: t("reportsPage.priceChangesKindProduct"),
        label: t("reportsPage.priceChangesKindProduct"),
      },
      {
        value: t("reportsPage.priceChangesKindAddition"),
        label: t("reportsPage.priceChangesKindAddition"),
      },
    ],
    [t],
  );

  const statusOptions = useMemo<ChecklistOption[]>(
    () => [
      {
        value: t("reportsPage.priceChangesActive"),
        label: t("reportsPage.priceChangesActive"),
      },
      {
        value: t("reportsPage.priceChangesReverted"),
        label: t("reportsPage.priceChangesReverted"),
      },
    ],
    [t],
  );

  return useMemo(
    () => [
      helper.accessor((row) => row.changedAt, {
        id: "changed_at",
        header: t("reportsPage.discountsTime"),
        size: 104,
        meta: {
          // Датата под часа само при многодневен период — иначе повтаря
          // избраното в лентата горе на всеки ред.
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "never" },
          excel: { format: "date" },
        },
      }),

      helper.accessor((row) => row.userLabel, {
        id: "user",
        header: t("reportsPage.user"),
        size: 140,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: userOptions },
          excel: { format: "text" },
        },
      }),

      // Номерът на сметката стои под името на масата: по него се разпознава
      // редът в „Справка сметки" и в одита, а името е това, което се помни.
      helper.accessor(
        (row): TextCellValue => ({
          primary: row.billLabel,
          secondary: `#${row.billId}`,
        }),
        {
          id: "bill",
          header: t("reportsPage.table"),
          size: 116,
          meta: {
            cell: { kind: "text", lines: 2 },
            filter: { kind: "text" },
            alwaysVisible: true,
            mobileRole: "title",
            excel: { format: "text" },
          },
        },
      ),

      // „Добавка" е вторият ред на клетката, а не собствена колона на всеки
      // екран: иначе една дума заема място, което в 9 от 10 реда е празно.
      // Колоната „Вид" долу пази филтъра по същия признак.
      helper.accessor(
        (row): TextCellValue => ({
          primary: row.itemLabel,
          secondary: row.additionHint,
        }),
        {
          id: "item",
          header: t("reportsPage.product"),
          size: 200,
          meta: {
            cell: { kind: "text", lines: 2 },
            filter: { kind: "checklist", options: itemOptions },
            alwaysVisible: true,
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.quantity, {
        id: "quantity",
        header: t("reportsPage.qty"),
        size: 96,
        meta: {
          cell: { kind: "num" },
          hideBelow: "tablet",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.oldPrice, {
        id: "old_price",
        header: t("reportsPage.priceChangesOldPrice"),
        size: 116,
        // БЕЗ `hideBelow`: „Нова цена" без „Стара цена" не казва нищо — двете
        // се четат само една до друга и остават заедно и на таблет.
        meta: {
          cell: { kind: "money" },
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.newPrice, {
        id: "new_price",
        header: t("reportsPage.priceChangesNewPrice"),
        size: 116,
        meta: {
          cell: { kind: "money", strong: true },
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.directionLabel, {
        id: "direction",
        header: t("reportsPage.priceChangesDirection"),
        size: 130,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: directionOptions },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // Разликата НА ЕДИНИЦА, с процента под нея. Собствен рендер, защото
      // знакът стои пред сумата и се чете в двете посоки.
      helper.accessor((row) => row.difference, {
        id: "difference",
        header: t("reportsPage.priceChangesDelta"),
        size: 128,
        cell: (context) => (
          <PriceChangeAmount
            value={context.getValue()}
            percent={context.row.original.percent}
          />
        ),
        meta: {
          label: t("reportsPage.priceChangesDelta"),
          align: "right",
          excel: { format: "money" },
        },
      }),

      // Разликата × количеството — отражението върху сметката. Сборът в реда
      // „Общо" е НЕТО ефектът: намаленията и увеличенията се гасят взаимно, а
      // двете суми поотделно стоят в кутийките горе.
      helper.accessor((row) => row.lineDifference, {
        id: "line_difference",
        header: t("reportsPage.priceChangesLineEffect"),
        size: 138,
        cell: (context) => <PriceChangeAmount value={context.getValue()} />,
        meta: {
          label: t("reportsPage.priceChangesLineEffect"),
          totalCell: { kind: "money" },
          total: "sum",
          align: "right",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),

      // Изгасени по подразбиране: продуктовите редове и активните промени са
      // правилото, а не новината. Стоят в панела „Колони" заради филтрите си.
      helper.accessor((row) => row.kindLabel, {
        id: "kind",
        header: t("reportsPage.priceChangesColKind"),
        size: 110,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: kindOptions },
          defaultHidden: true,
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.statusLabel, {
        id: "status",
        header: t("reportsPage.status"),
        size: 110,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: statusOptions },
          defaultHidden: true,
          excel: { format: "text" },
        },
      }),
    ],
    [
      t,
      isMultiDay,
      userOptions,
      itemOptions,
      directionOptions,
      kindOptions,
      statusOptions,
    ],
  );
};
